import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import { PrimaryButton, SecondaryButton, ChevronLeftIcon } from './ui'
import { useApi } from '../services/api'
import { clearClaimDraft } from '../claims/claimDraftStorage'
import { clearLegalToDriveConfirmed } from '../claims/claimLegalToDriveGate'
import PolicyTabNav from './PolicyTabNav'
import PolicyBlockedMessageBody from './PolicyBlockedMessageBody'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import { POLICIES_LIST_CACHE_KEY, readPolicyFromListCache } from '../utils/policiesListCache'
import {
  formatSubmittedClaimStatusLabel,
  formatSubmittedClaimSubmissionDate,
  submittedClaimLossTypeLabel,
} from '../claims/submittedClaimDisplay'
import type { PolicyLink, SubmittedClaimListItem, SubmittedClaimStatusResponse } from '../services/types'

interface PolicySummary {
  policyRef: string
  regoNumber: string
  vehicleName: string
  renewalDate: string
  policyNumber: string
}

function policyLinkToSummary(policy: PolicyLink): PolicySummary {
  const policyNumber = policy.policyNumber || policy.policyRef
  const regoNumber = policy.registrationNumber || policyNumber
  const vehicleName = policy.vehicleName || `Policy ${policy.policyRef}`

  let renewalDate: string
  if (policy.renewalDateFormatted) {
    renewalDate = policy.renewalDateFormatted
  } else if (policy.renewalDate) {
    renewalDate = new Date(policy.renewalDate).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } else {
    const linkedDate = new Date(policy.linkedAt)
    linkedDate.setFullYear(linkedDate.getFullYear() + 1)
    renewalDate = linkedDate.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  return {
    policyRef: policy.policyRef,
    regoNumber,
    vehicleName,
    renewalDate,
    policyNumber,
  }
}

interface ClaimSummary {
  id: string
  lossType: string
  claimNumber: string
  submittedAt: string
}

function ClaimDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm relative shrink-0 text-sm w-full">
      <p className="font-body font-regular text-text-tertiary-600 relative shrink-0">
        {label}
      </p>
      <p className="font-body font-medium text-text-secondary-700 relative shrink-0">
        {value}
      </p>
    </div>
  )
}

function ClaimCard({
  lossType,
  claimNumber,
  submittedAt,
  liveStatusAvailable,
}: ClaimSummary & { liveStatusAvailable: boolean }) {
  const { getSubmittedClaimStatus } = useApi()
  const [live, setLive] = useState<SubmittedClaimStatusResponse | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)

  const handleCheckStatus = async () => {
    setStatusLoading(true)
    setStatusError(null)
    try {
      const result = await getSubmittedClaimStatus(claimNumber)
      setLive(result)
    } catch {
      setStatusError("Couldn't load claim status. Try again.")
    } finally {
      setStatusLoading(false)
    }
  }

  return (
    <article className="bg-background-primary border border-border-primary border-solid flex flex-col isolate items-start relative rounded-2xl shadow-xs w-full">
      <div className="flex flex-col gap-[8px] items-start px-[16px] py-[20px] relative shrink-0 w-full z-[1]">
        <h3 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900 w-full">
          {lossType}
        </h3>
        <ClaimDetailRow label="Claim number" value={claimNumber} />
        <ClaimDetailRow label="Date of submission" value={submittedAt} />
        {liveStatusAvailable ? (
          <>
            {live?.available ? (
              <>
                <ClaimDetailRow label="Status" value={formatSubmittedClaimStatusLabel(live.status)} />
                <ClaimDetailRow label="Decision" value={formatSubmittedClaimStatusLabel(live.decision)} />
                {live.primaryLossCode ? (
                  <ClaimDetailRow label="Loss code" value={live.primaryLossCode} />
                ) : null}
                {live.externalNumber ? (
                  <ClaimDetailRow label="Portal reference" value={live.externalNumber} />
                ) : null}
              </>
            ) : live && !live.available ? (
              <p className="m-0 font-body font-regular text-sm leading-sm text-text-tertiary-600 w-full">
                {live.message || 'Live status is not available for this claim.'}
              </p>
            ) : null}
            {statusError ? (
              <p
                className="m-0 font-body font-regular text-sm leading-sm text-text-tertiary-600 w-full"
                role="alert"
              >
                {statusError}
              </p>
            ) : null}
            <SecondaryButton
              type="button"
              size="sm"
              disabled={statusLoading}
              onClick={() => {
                void handleCheckStatus()
              }}
            >
              {statusLoading ? 'Checking…' : 'Check status'}
            </SecondaryButton>
          </>
        ) : null}
      </div>
    </article>
  )
}

export default function ClaimsLanding() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getPolicies, getSubmittedClaims } = useApi()
  const [claimsLoading, setClaimsLoading] = useState(true)
  const [submittedClaimsRaw, setSubmittedClaimsRaw] = useState<SubmittedClaimListItem[]>([])
  const [liveStatusAvailable, setLiveStatusAvailable] = useState(false)
  const [policySummary, setPolicySummary] = useState<PolicySummary | null>(null)
  /** Full list row for claims steps — avoid duplicate getPolicies on /claims/start */
  const [policyForNextStep, setPolicyForNextStep] = useState<PolicyLink | null>(null)


  useEffect(() => {
    let cancelled = false

    const applyPolicy = (policy: PolicyLink) => {
      setPolicyForNextStep(policy)
      setPolicySummary(policyLinkToSummary(policy))
    }

    const params = new URLSearchParams(location.search)
    const requestedPolicyRef = decodePolicyRef(params.get('policyRef'))
    const incomingPolicy = (location.state as { policy?: PolicyLink } | null)?.policy

    if (incomingPolicy && (!requestedPolicyRef || incomingPolicy.policyRef === requestedPolicyRef)) {
      if (!cancelled) {
        applyPolicy(incomingPolicy)
      }
      // Continue to refresh so claimsBlocked stays accurate.
    }

    if (requestedPolicyRef) {
      const fromListCache = readPolicyFromListCache(requestedPolicyRef)
      if (fromListCache) {
        if (!cancelled) {
          applyPolicy(fromListCache)
        }
        // Still refresh so claimsBlocked (future term start) is current after deploy / cache age.
      }
    }

    const fetchPolicies = async () => {
      try {
        const response = await getPolicies()
        if (cancelled) return

        if (!response.policies || response.policies.length === 0) {
          setPolicyForNextStep(null)
          setPolicySummary(null)
          return
        }

        const matchingPolicy = requestedPolicyRef
          ? response.policies.find((item) => item.policyRef === requestedPolicyRef)
          : response.policies[0]

        const policy = (matchingPolicy || response.policies[0]) as PolicyLink
        applyPolicy(policy)
      } catch (error) {
        console.error('Error fetching policies for claims:', error)
        if (!cancelled) {
          setPolicyForNextStep(null)
          setPolicySummary(null)
        }
      }
    }

    void fetchPolicies()

    return () => {
      cancelled = true
    }
  }, [getPolicies, location.search, (location.state as { policy?: PolicyLink } | null)?.policy?.policyRef])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        if (!cancelled) setClaimsLoading(true)
        const res = await getSubmittedClaims()
        if (cancelled) return
        setSubmittedClaimsRaw(Array.isArray(res.claims) ? res.claims : [])
        setLiveStatusAvailable(res.liveStatusAvailable === true)
      } catch (e) {
        console.error('Error fetching submitted claims:', e)
        if (!cancelled) {
          setSubmittedClaimsRaw([])
          setLiveStatusAvailable(false)
        }
      } finally {
        if (!cancelled) setClaimsLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [getSubmittedClaims])

  const policyRefFromQuery = decodePolicyRef(new URLSearchParams(location.search).get('policyRef'))
  const targetPolicyRef = policySummary?.policyRef || policyRefFromQuery

  const claims: ClaimSummary[] = useMemo(() => {
    const list = targetPolicyRef
      ? submittedClaimsRaw.filter((c) => c.policyRef === targetPolicyRef)
      : submittedClaimsRaw
    return list.map((c) => ({
      id: c.id,
      lossType: submittedClaimLossTypeLabel(c.source),
      claimNumber: c.claimNumber,
      submittedAt: formatSubmittedClaimSubmissionDate(c.createdAt),
    }))
  }, [submittedClaimsRaw, targetPolicyRef])

  const handleMakeClaim = () => {
    clearClaimDraft()
    clearLegalToDriveConfirmed()
    if (targetPolicyRef) {
      const fromIncoming = (location.state as { policy?: PolicyLink } | null)?.policy
      const fromFetch = policyForNextStep
      const fromCache = readPolicyFromListCache(targetPolicyRef)
      const policy = (fromFetch?.policyRef === targetPolicyRef && fromFetch) || fromIncoming || fromCache
      navigate(`/claims/start?policyRef=${encodeURIComponent(targetPolicyRef)}`, {
        state: policy ? { policy } : undefined,
      })
    } else {
      navigate('/claims/start')
    }
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      <header className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[24px] items-start max-w-[768px] relative shrink-0 w-full">
            <button
              onClick={() => {
                sessionStorage.removeItem(POLICIES_LIST_CACHE_KEY)
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>

            <h1 className="sr-only">Claims</h1>

            {policySummary && (
              <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                <div className="content-start flex flex-wrap gap-[4px] items-start relative shrink-0 w-full">
                  <div className="bg-background-primary border border-text-primary-900 border-solid flex items-center px-[8px] py-[4px] relative rounded-xs shrink-0">
                    <p className="font-body font-medium text-md leading-md text-text-primary-900 text-center">
                      {policySummary.regoNumber}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <div className="flex gap-[8px] items-center relative shrink-0 w-full">
                    <p className="flex-1 font-display font-regular text-display-sm leading-[38px] text-text-primary-on-brand min-w-0 relative shrink-0">
                      {policySummary.vehicleName}
                    </p>
                  </div>
                  <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                    <p className="font-body font-regular relative shrink-0">
                      Renews on{' '}
                    </p>
                    <p className="font-body font-medium relative shrink-0">
                      {policySummary.renewalDate}
                    </p>
                  </div>
                  <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                    <p className="font-body font-regular relative shrink-0">
                      Policy Number{' '}
                    </p>
                    <p className="font-body font-semibold relative shrink-0">
                      {policySummary.policyNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main
        id="main"
        className="flex flex-col md:flex-row gap-[32px] items-start max-w-container-desktop px-[16px] md:px-[32px] py-[32px] md:py-[48px] relative shrink-0 w-full mx-auto"
      >
        <PolicyTabNav
          policyId={targetPolicyRef ?? undefined}
          activeTab="claims"
          policyForClaimsNav={policyForNextStep}
        />

        <div className="flex flex-1 flex-col gap-[24px] md:gap-[32px] items-center max-w-[768px] min-w-0 relative shrink-0 w-full">
          <section aria-labelledby="make-claim-heading" className="flex flex-col gap-[12px] items-start w-full">
            {policyForNextStep?.claimsBlocked === true ? (
              <div
                role="status"
                className="bg-background-primary border border-border-primary border-solid flex flex-col gap-[8px] items-start px-[24px] py-[20px] relative rounded-2xl shadow-xs w-full"
              >
                <h2 id="make-claim-heading" className="sr-only">
                  Claims unavailable until cover starts
                </h2>
                <PolicyBlockedMessageBody
                  message={
                    policyForNextStep.claimsBlockedMessage?.trim() ||
                    "Online claims aren't available until your policy cover starts.\n\nYou can still manage your policy online. If you need help, call us on 1300 296 296."
                  }
                  className="font-body font-regular text-sm leading-sm text-text-secondary-700"
                />
              </div>
            ) : (
              <PrimaryButton
                onClick={handleMakeClaim}
                size="lg"
                fullWidth
                aria-label="Make a claim"
                className="!bg-[#F3DC6B] !text-text-primary-900"
              >
                Make a claim
              </PrimaryButton>
            )}
          </section>

          <section
            aria-labelledby="previously-submitted-online-claims-heading"
            className="flex flex-col gap-[16px] items-start w-full"
          >
            <h2
              id="previously-submitted-online-claims-heading"
              className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              Previously Submitted Online Claims
            </h2>
            {claimsLoading ? (
              <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[24px] py-[40px] relative rounded-2xl shadow-xs w-full">
                <LoadingIcon size="medium" message="Loading claims..." />
              </div>
            ) : claims.length > 0 ? (
              <div className="flex flex-col gap-[12px] items-start relative w-full">
                {claims.map((claim) => (
                  <ClaimCard key={claim.id} {...claim} liveStatusAvailable={liveStatusAvailable} />
                ))}
              </div>
            ) : (
              <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[24px] py-[40px] relative rounded-2xl shadow-xs w-full">
                <div className="flex flex-col gap-[8px] items-center text-center">
                  <p className="font-body font-semibold text-md leading-md text-text-primary-900">
                    {targetPolicyRef ? 'No claims for this policy yet' : 'No claims submitted through the portal yet'}
                  </p>
                  <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 max-w-[320px]">
                    {targetPolicyRef
                      ? 'Claims you submit through this portal for this vehicle will appear here.'
                      : 'Claims you submit through this portal will appear here.'}
                  </p>
                </div>
              </div>
            )}
          </section>

          <section aria-labelledby="claims-contact-heading" className="flex flex-col gap-[6px] items-start w-full">
            <h2 id="claims-contact-heading" className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900">
              Can't see your claim?
            </h2>
            <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
              Call us on{' '}
              <a
                href="tel:1300296296"
                className="font-body font-regular text-sm leading-sm text-text-secondary-700 underline"
              >
                1300 296 296
              </a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}
